import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { createUseStyles, Button, Stepper } from 'hoi-poi-ui';
import { useWeb5Context } from "../../../lib/Web5Context";
import { TransitionGroup, FadeTransition } from "../../../components/Transitions";
import { Layout } from "../components";
import { InviteUsers, SegmentAccounts, OrdersStates, SetupPipeline, Summary } from "./components";
import { TABLES } from "../constants";
import styles from "./styles.js";
const useStyles = createUseStyles(styles, 'OnboardingWizard');
const trackingEvents = ['firstStepOnboarding', 'secondStepOnboarding', 'thirdStepOnboarding', 'fourthStepOnboarding', 'fifthStepOnboarding'];
const OnboardingWizard = _ref => {
  let {
    logo,
    getUsers,
    setUsers,
    getList,
    setList,
    userRoles,
    handleError,
    customEmailValidation,
    productModeCode,
    onSubmit,
    settings,
    logEvent
  } = _ref;
  const isFirstLoad = useRef(true);
  const currentStep = useRef(0);
  const [state, setState] = useState({
    loading: false
  });
  const [steps, setSteps] = useState([{
    isCompleted: false
  }, {
    isCompleted: false
  }, {
    isCompleted: false
  }, {
    isCompleted: false
  }, {
    isCompleted: false
  }]);
  const [count, setCount] = useState({
    [TABLES.ACCOUNT_TYPES]: 0,
    [TABLES.INVITED_USERS]: 0,
    [TABLES.OPPORTUNITY_STATUS]: 0,
    [TABLES.SALES_ORDER_STATUS]: 0
  });
  const classes = useStyles();
  const {
    t
  } = useWeb5Context();
  const handleTracking = useCallback(() => {
    logEvent && logEvent({
      event: 'sageCRM',
      functionality: trackingEvents[currentStep.current]
    });
  }, [logEvent]);
  useEffect(() => {
    if (!isFirstLoad.current) return;
    isFirstLoad.current = false;
    handleTracking();
  }, [handleTracking]);
  const handleSubmit = useCallback(() => {
    setState(state => _objectSpread(_objectSpread({}, state), {}, {
      loading: true
    }));
    onSubmit && onSubmit();
  }, [onSubmit]);
  const handleStepChange = useCallback(function (e, delta) {
    if (delta === void 0) {
      delta = 1;
    }
    e.preventDefault();
    currentStep.current = currentStep.current + delta;
    const newSteps = steps.reduce((arr, step, index) => [...arr, _objectSpread(_objectSpread({}, step), {}, {
      isCompleted: currentStep.current > index
    })], []);
    setSteps(newSteps);
    if (currentStep.current === steps.length) {
      handleSubmit();
    } else {
      handleTracking();
    }
  }, [handleSubmit, handleTracking, steps]);
  const handleSetCount = useCallback((key, value) => setCount(_objectSpread(_objectSpread({}, count), {}, {
    [key]: value
  })), [count]);
  const inviteUsersProps = useMemo(() => ({
    getUsers,
    setUsers,
    userRoles,
    customEmailValidation,
    handleError,
    productModeCode,
    handleSetCount
  }), [customEmailValidation, getUsers, setUsers, userRoles, handleError, productModeCode, handleSetCount]);
  const listsProps = useMemo(() => ({
    getList,
    setList,
    handleError,
    handleSetCount
  }), [getList, handleError, setList, handleSetCount]);
  const getStep = useCallback(step => {
    step = step >= steps.length ? steps.length - 1 : step;
    return [/*#__PURE__*/React.createElement(InviteUsers, inviteUsersProps), /*#__PURE__*/React.createElement(SegmentAccounts, listsProps), /*#__PURE__*/React.createElement(OrdersStates, listsProps), /*#__PURE__*/React.createElement(SetupPipeline, listsProps), /*#__PURE__*/React.createElement(Summary, {
      count: count,
      settings: settings === null || settings === void 0 ? void 0 : settings.summary
    })][step];
  }, [inviteUsersProps, listsProps, steps.length, count, settings]);
  const getNavActions = useMemo(() => {
    return /*#__PURE__*/React.createElement("div", {
      className: classes.nav
    }, currentStep.current > 0 && /*#__PURE__*/React.createElement(Button, {
      size: "big",
      type: "secondary",
      icon: "arrowBack",
      onClick: e => handleStepChange(e, -1),
      isDisabled: state.loading
    }, t('action_back')), /*#__PURE__*/React.createElement(Button, {
      size: "big",
      type: "primary",
      icon: state.loading ? null : 'arrowForward',
      iconPosition: "right",
      onClick: handleStepChange,
      isLoading: state.loading
    }, t(currentStep.current < steps.length - 1 ? 'action_next' : 'action_start_enjoy')));
  }, [steps.length, classes.nav, t, state.loading, handleStepChange]);
  return /*#__PURE__*/React.createElement(Layout, {
    className: classes.layout
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.logo
  }, logo), /*#__PURE__*/React.createElement(Stepper, {
    steps: steps,
    currentStep: currentStep.current,
    withoutLabels: true,
    className: classes.stepper
  }), /*#__PURE__*/React.createElement(TransitionGroup, null, /*#__PURE__*/React.createElement(FadeTransition, {
    transitionKey: `${currentStep.current}`,
    show: true
  }, getStep(currentStep.current))), getNavActions);
};
export default /*#__PURE__*/memo(OnboardingWizard);